import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Counter from '../components/Counter';

const { FiAward, FiUsers, FiGlobe, FiTrendingUp, FiShield, FiBookOpen } = FiIcons;

const Team = () => {
  const certifications = [
    {
      icon: FiAward,
      title: 'Auditeurs et Consultants IRCA / CQI',
      description: 'Nos principaux collaborateurs sont accrédités par l\'IRCA (International Register of Certificated Auditors) et CQI (Chartered Quality Institute), garantissant un audit de qualité reconnu mondialement pour les normes ISO 9001, ISO 14001 et ISO 45001.'
    },
    {
      icon: FiShield,
      title: 'Experts Sectoriels Certifiés',
      description: 'Nous disposons d\'experts spécialisés dans les domaines du secteur manufacturier, des services, du secteur minier, de la construction et des entreprises publiques, chacun disposant de certifications métier reconnues internationalement.'
    },
    {
      icon: FiTrendingUp,
      title: 'Facilitateurs du Changement Accrédités',
      description: 'Notre équipe de facilitateurs est formée aux meilleures pratiques de conduite du changement organisationnel selon les standards internationaux.'
    }
  ];

  const expertise = [
    {
      domain: 'Management de la Qualité',
      certifications: ['ISO 9001 Lead Auditor', 'CQI Certified Quality Professional'],
      experience: '10+ années d\'expérience'
    },
    {
      domain: 'Management Environnemental',
      certifications: ['ISO 14001 Lead Auditor', 'Environmental Management Specialist'],
      experience: '8+ années d\'expérience'
    },
    {
      domain: 'Santé et Sécurité au Travail',
      certifications: ['ISO 45001 Lead Auditor', 'OHSAS 18001 Transition Expert'],
      experience: '12+ années d\'expérience'
    },
    {
      domain: 'Sécurité de l\'Information',
      certifications: ['ISO 27001 Lead Auditor', 'Information Security Professional'],
      experience: '6+ années d\'expérience'
    }
  ];

  const values = [
    {
      icon: FiBookOpen,
      title: 'Formation Continue',
      description: 'Nos professionnels bénéficient d\'une formation continue régulière pour maintenir et actualiser leurs compétences.'
    },
    {
      icon: FiGlobe,
      title: 'Réseaux Internationaux',
      description: 'Participation active aux réseaux professionnels internationaux pour rester à la pointe des meilleures pratiques.'
    },
    {
      icon: FiUsers,
      title: 'Approche Collaborative',
      description: 'Travail en équipe multidisciplinaire pour offrir une expertise complète et intégrée à nos clients.'
    }
  ];

  const stats = [
    { value: 15, suffix: '+', label: 'Experts Certifiés' },
    { value: 50, suffix: '+', label: 'Certifications' },
    { value: 10, suffix: '+', label: 'Années d\'Expérience' },
    { value: 100, suffix: '%', label: 'Excellence' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-16 lg:pt-20"
    >
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-700 dark:from-blue-950 dark:to-blue-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Notre Équipe</h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Excellence et Certification Internationale
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-white mb-6">
              Des Professionnels d'Excellence
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto">
              M.A.M Consulting s'appuie sur une équipe de professionnels expérimentés, certifiés selon les standards internationaux les plus exigeants.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-800 dark:to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-white mb-4">
              Certifications Internationales
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Standards mondiaux de qualité et d'excellence
            </p>
          </motion.div>

          <div className="space-y-8">
            {certifications.map((cert, index) => (
              <motion.div
                key={index}
                initial={{ x: index % 2 === 0 ? -50 : 50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg"
              >
                <div className="flex flex-col lg:flex-row items-start lg:items-center space-y-4 lg:space-y-0 lg:space-x-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={cert.icon} className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-3">{cert.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-lg">{cert.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Domains */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-white mb-4">
              Domaines d'Expertise
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Spécialisations et expérience reconnue
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {expertise.map((domain, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all"
              >
                <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">{domain.domain}</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">Certifications :</p>
                    {domain.certifications.map((cert, idx) => (
                      <span
                        key={idx}
                        className="inline-block bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 text-xs px-3 py-1 rounded-full mr-2 mb-2"
                      >
                        {cert}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiTrendingUp} className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm text-gray-700 dark:text-gray-200">{domain.experience}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gradient-to-br from-blue-700 to-blue-900 dark:from-blue-900 dark:to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Notre Engagement Professionnel
            </h2>
            <p className="text-xl text-blue-100">
              L'excellence par la formation continue et l'innovation
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{value.title}</h3>
                <p className="text-blue-100">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats with Animation */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0.5, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: false }} // Permet de rejouer l'animation de la carte au scroll
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-blue-700 dark:text-blue-400 mb-2">
                  <Counter to={stat.value} suffix={stat.suffix} duration={2.5} />
                </div>
                <div className="text-gray-600 dark:text-gray-400">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default Team;